import React, { useContext, useEffect, useRef, useState } from 'react';
import { ModalFormLayout } from '../../components/styled/layout';
import { Box, Flex } from 'rebass';
import { Button } from '../../components/styled/button';
import { useHistory } from 'react-router-dom';
import { ReactComponent as Flower } from '../../images/flower.svg';
import { Formik, useFormikContext, FormikValues } from 'formik';
import { TrackSessionForm } from './TrackSessionForm';
import { UserContext } from '../../contexts/UserContext';
import { apiBaseUrl } from '../../lib/apiUrls';
import client from '@cssat/acorn-api-client';
import {
  adaptCreateTrackedSession,
  CreateTrackedSessionAdapter,
} from './formApiAdapters';
import {
  TrackSessionSchema,
  FormFields,
  initialValues,
} from './TrackSessionForm/validation';
import { HeaderToast } from '../../components/styled/toast';
import { gridColumns, space } from '../../theme';
import { CreateTrackedSession } from '../../types/api';

export const TrackSession: React.FC = () => {
  const history = useHistory();
  const { user } = useContext(UserContext);
  const useStep = useState<number>(1);
  // eslint-disable-next-line
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [showSubmitError, setShowSubmitError] = useState<boolean>(false);
  let adaptFormToApi = useRef<CreateTrackedSessionAdapter | null>(null);
  useEffect(() => {
    if (!user) {
      history.push('/services');
    } else {
      adaptFormToApi.current = adaptCreateTrackedSession(user);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const formRef = useRef<FormikValues | null>();

  const onSubmit = async (values: FormFields) => {
    const { ServiceDelivery } = client.init(apiBaseUrl());
    setShowSubmitError(false);
    setHasSubmitted(true);
    let sanitizedData: CreateTrackedSession;
    if (!adaptFormToApi || !adaptFormToApi.current) {
      return;
    }
    sanitizedData = await adaptFormToApi.current(values);

    try {
      await ServiceDelivery.createTrackedSession(sanitizedData);
      history.push('/services');
    } catch {
      // eslint-disable-next-line no-console
      console.log('*** TRIED TO SUBMIT:', JSON.stringify({ data: values }));
      setHasSubmitted(false);
      setShowSubmitError(true);
    }
  };

  const HeaderButtons: React.FC<{ useStep: React.ComponentState }> = ({
    useStep,
  }) => {
    const { submitForm } = useFormikContext<FormFields>();
    const { values } = useFormikContext<FormFields>();
    const [step, setStep] = useStep;

    return (
      <>
        <Flex flexDirection="row" flexWrap={['wrap', 'wrap', 'nowrap']}>
          <Button
            display={['none', 'none', 'initial']}
            variant={'text'}
            onClick={() => history.push('/services')}
          >
            Cancel
          </Button>
          <Button
            width={['100%', '100%', 'initial']}
            sx={{ flexGrow: '1' }}
            display={['initial', 'initial', 'none']}
            variant={'secondary'}
            onClick={() => history.push('/services')}
          >
            Cancel
          </Button>
          {step === 1 && values.service && (
            <Button
              width={['100%', '100%', 'initial']}
              mt={[space.one, space.one, 0]}
              sx={{ flexGrow: '1' }}
              disabled={hasSubmitted}
              type="button"
              onClick={() => {
                setStep(2);
              }}
            >
              Next
            </Button>
          )}
          {step > 1 && (
            <Button
              width={['100%', '100%', 'initial']}
              mt={[space.one, space.one, 0]}
              sx={{ flexGrow: '1' }}
              disabled={hasSubmitted}
              type="submit"
              onClick={() => {
                submitForm();
              }}
            >
              Track service
            </Button>
          )}
        </Flex>
      </>
    );
  };

  return (
    <Formik
      validationSchema={TrackSessionSchema}
      onSubmit={(values: FormFields) => {
        onSubmit(values);
      }}
      initialValues={initialValues}
      innerRef={theRef => {
        formRef.current = theRef;
      }}
    >
      <ModalFormLayout
        sidebarChildren={
          <Box mt={200} display={['none', 'none', 'block']}>
            <Flower />
          </Box>
        }
        headerChildren={<HeaderButtons useStep={useStep} />}
        title="Track a service"
      >
        {showSubmitError && (
          <HeaderToast
            data-testid="trackSessionErrorToast"
            variant="warning"
            mt={space.ten}
            ml={`-${space.quarter}`}
          >
            <strong>Sorry, we can’t track this session right now. </strong>
            Please check your connection and try again.
          </HeaderToast>
        )}
        <Box maxWidth={['100%', '100%', gridColumns(5)]}>
          <TrackSessionForm useStep={useStep} />
        </Box>
      </ModalFormLayout>
    </Formik>
  );
};
